import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { LOGO_URL, API } from "@/lib/constants";

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Successfully subscribed to our newsletter!");
      setEmail('');
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#050505] border-t border-white/5" data-testid="footer">
      <div className="container-custom py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="block mb-6">
              <img src={LOGO_URL} alt="Digital Media Ninja" className="h-10 w-auto" />
            </Link>
            <p className="text-gray-400 text-sm mb-6">
              Dallas&apos; premier digital marketing agency specializing in AEO, GEO, and Local SEO. 
              Future-proof your business with AI-powered search optimization.
            </p>
            <div className="space-y-3 mb-6">
              <a href="tel:+14329349470" className="flex items-center gap-3 text-gray-400 hover:text-[#00E676] transition-colors">
                <Phone className="w-4 h-4" />
                <span>(432) 934-9470</span>
              </a>
              <a href="mailto:info@digitalmedianinja.com" className="flex items-center gap-3 text-gray-400 hover:text-[#00E676] transition-colors">
                <Mail className="w-4 h-4" />
                <span>info@digitalmedianinja.com</span>
              </a>
              <a 
                href="https://www.google.com/maps/place/Digital+Media+Ninja/@32.7976692,-96.814698,17z" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-gray-400 hover:text-[#00E676] transition-colors"
              >
                <MapPin className="w-4 h-4 mt-1" />
                <span>3333 Harry Hines Blvd<br />Dallas, TX 75201</span>
              </a>
            </div>
            
            {/* Google Maps Embed */}
            <div className="rounded-xl overflow-hidden border border-white/10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3354.0!2d-96.814698!3d32.7976692!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xac4e03f899bc597%3A0x2d34331c3556b930!2sDigital%20Media%20Ninja!5e0!3m2!1sen!2sus!4v1707000000000!5m2!1sen!2sus"
                width="100%"
                height="150"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Digital Media Ninja Location"
                data-testid="google-maps-embed"
              />
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-white font-bold mb-6">Services</h4>
            <ul className="space-y-3">
              {[
                { name: "Local SEO Dallas", path: "/services/local-seo" },
                { name: "GBP Optimization", path: "/services/gbp-optimization" },
                { name: "PPC Advertising", path: "/services/ppc" },
                { name: "AEO Services", path: "/aeo-geo" },
                { name: "GEO Services", path: "/aeo-geo" },
                { name: "Gym Websites", path: "/gym-website-design" },
                { name: "Web Design", path: "/services/web-design" }
              ].map((service) => (
                <li key={service.path + service.name}>
                  <Link 
                    to={service.path}
                    className="text-gray-400 hover:text-[#00E676] transition-colors text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries Column */}
          <div>
            <h4 className="text-white font-bold mb-6">Industries</h4>
            <ul className="space-y-3">
              {[
                { name: "Fitness & Gyms", slug: "gym-marketing" },
                { name: "HVAC Companies", slug: "hvac-marketing" },
                { name: "Landscaping", slug: "landscaping-marketing" },
                { name: "Law Firms", slug: "law-firm-marketing" },
                { name: "Healthcare", slug: "healthcare-marketing" },
                { name: "Real Estate", slug: "real-estate-marketing" }
              ].map((industry) => (
                <li key={industry.slug}>
                  <Link 
                    to={`/${industry.slug}`}
                    className="text-gray-400 hover:text-[#00E676] transition-colors text-sm"
                  >
                    {industry.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Column */}
          <div>
            <h4 className="text-white font-bold mb-6">Stay Updated</h4>
            <p className="text-gray-400 text-sm mb-4">
              Get weekly insights on AEO, GEO, and AI-powered marketing strategies.
            </p>
            <form onSubmit={handleNewsletterSubmit} id="newsletter-form" name="newsletter-form" className="space-y-3">
              <Input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                data-testid="newsletter-email"
              />
              <Button 
                type="submit" 
                className="btn-ninja w-full"
                disabled={isSubmitting}
                data-testid="newsletter-submit"
              >
                {isSubmitting ? "Subscribing..." : "Subscribe"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5 py-6">
        <div className="container-custom flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © 2026 Digital Media Ninja. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-2">
            <Link to="/case-studies" className="text-gray-500 hover:text-gray-400 text-sm" data-testid="footer-case-studies-link">Case Studies</Link>
            <a href="/insights/" className="text-gray-500 hover:text-gray-400 text-sm">AI Search Insights</a>
            <Link to="/checklists" className="text-gray-500 hover:text-gray-400 text-sm" data-testid="footer-checklists-link">Free Checklists</Link>
            <Link to="/privacy-policy" className="text-gray-500 hover:text-gray-400 text-sm">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-gray-500 hover:text-gray-400 text-sm">Terms of Service</Link>
            <Link to="/editorial-policy" className="text-gray-500 hover:text-gray-400 text-sm" data-testid="footer-editorial-policy-link">Editorial Policy</Link>
            <Link to="/brand-guidelines" className="text-gray-500 hover:text-gray-400 text-sm">Brand Guidelines</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
